import { PoseData, GestureEvent, GestureType } from '../../../types';
import { areCollinear, angleFromHorizontal, distance, isVisible } from '../../../utils/geometry';

/**
 * ArmExtendedDetector detects when an arm is extended horizontally
 * Validates Requirements 3.3.3
 */
export class ArmExtendedDetector {
  // Angle threshold for collinearity (degrees)
  private static readonly COLLINEAR_THRESHOLD = 30;
  
  // Angle threshold for horizontal (±30 degrees from horizontal)
  private static readonly HORIZONTAL_THRESHOLD = 30;
  
  // Minimum extension distance (normalized coordinates)
  private static readonly MIN_EXTENSION = 0.15;
  
  /**
   * Detect if left arm is extended
   * @param pose Current pose data
   * @param timestamp Current timestamp
   * @returns GestureEvent if detected, null otherwise
   */
  detectLeft(pose: PoseData, timestamp: number): GestureEvent | null {
    return this.detectArm(pose, timestamp, 'left');
  }
  
  /**
   * Detect if right arm is extended
   * @param pose Current pose data
   * @param timestamp Current timestamp
   * @returns GestureEvent if detected, null otherwise
   */
  detectRight(pose: PoseData, timestamp: number): GestureEvent | null {
    return this.detectArm(pose, timestamp, 'right');
  }
  
  /**
   * Detect if specified arm is extended
   * @param pose Current pose data
   * @param timestamp Current timestamp
   * @param side Which arm to check
   * @returns GestureEvent if detected, null otherwise
   */
  private detectArm(pose: PoseData, timestamp: number, side: 'left' | 'right'): GestureEvent | null {
    if (!pose.detected) return null;
    
    const wrist = side === 'left' ? pose.landmarks.leftWrist : pose.landmarks.rightWrist;
    const elbow = side === 'left' ? pose.landmarks.leftElbow : pose.landmarks.rightElbow;
    const shoulder = side === 'left' ? pose.landmarks.leftShoulder : pose.landmarks.rightShoulder;
    
    // Check if all required landmarks are visible
    if (!isVisible(wrist) || !isVisible(elbow) || !isVisible(shoulder)) {
      return null;
    }
    
    // Check if wrist, elbow, shoulder are approximately collinear
    const isCollinear = areCollinear(shoulder, elbow, wrist, ArmExtendedDetector.COLLINEAR_THRESHOLD);
    if (!isCollinear) {
      return null;
    }
    
    // Check if arm is extended horizontally (±30 degrees from horizontal)
    const angle = Math.abs(angleFromHorizontal(shoulder, wrist));
    const isHorizontal = angle <= ArmExtendedDetector.HORIZONTAL_THRESHOLD || 
                         angle >= (180 - ArmExtendedDetector.HORIZONTAL_THRESHOLD);
    
    if (!isHorizontal) {
      return null;
    }
    
    // Check minimum extension distance (arm must be mostly straight)
    const extensionDistance = distance(shoulder, wrist);
    if (extensionDistance < ArmExtendedDetector.MIN_EXTENSION) {
      return null;
    }
    
    // Calculate confidence based on straightness and horizontal alignment
    // Straightness: how collinear the points are
    const actualAngle = 180 - Math.abs(180 - Math.abs(angleFromHorizontal(shoulder, elbow) - angleFromHorizontal(elbow, wrist)));
    const straightnessScore = Math.min(1.0, actualAngle / 170);
    
    // Horizontal alignment: how close to horizontal
    const horizontalScore = 1.0 - (Math.min(angle, 180 - angle) / ArmExtendedDetector.HORIZONTAL_THRESHOLD);
    
    // Extension: how far the arm is extended
    const extensionScore = Math.min(1.0, extensionDistance / 0.3);
    
    // Overall confidence is average of all scores
    const confidence = Math.max(0, Math.min(1, (straightnessScore + horizontalScore + extensionScore) / 3));
    
    const gestureType = side === 'left' ? GestureType.LEFT_ARM_EXTENDED : GestureType.RIGHT_ARM_EXTENDED;
    
    return {
      type: gestureType,
      confidence,
      timestamp
    };
  }
}
